import { useContext, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import useGlynt from './useGlynt';
import { AppContext } from '../../App';
import { getOffsetFromUrl } from '../../utils/getOffsetFromUrl';

import { FIELDS_PAGE_SIZE } from '../../constants';

export default function useGetAllFields(trainingSetId: string, limit: number = FIELDS_PAGE_SIZE) {
  const { dataPoolId } = useContext(AppContext);

  const glynt = useGlynt(dataPoolId);

  const { data, isError, isLoading, isFetching, fetchNextPage, hasNextPage, refetch } =
    useInfiniteQuery({
      queryKey: ['fields', trainingSetId],
      queryFn: async ({ pageParam }) => {
        return (await glynt).fields.list({
          advanced: true,
          training_set: trainingSetId,
          ordering: 'field_order',
          offset: pageParam,
          limit,
        });
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage) => {
        if (lastPage.next) {
          const offset = getOffsetFromUrl(lastPage.next);
          return offset;
        }
      },
      refetchOnWindowFocus: false,
      enabled: !!dataPoolId,
    });

  const currentPage = data?.pages?.length ?? 0;
  const totalPages = data?.pages?.[0]?.count ? Math.ceil(data.pages[0].count / limit) : 0;

  useEffect(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [data, hasNextPage, fetchNextPage]);

  return {
    data,
    error: isError,
    loading: isLoading,
    total: totalPages,
    isFetching,
    refetch,
  };
}
